import React, { useCallback, useEffect, useState } from 'react';
import { acordApi, fetchBlobUrl, apiErrorMessage } from '../../services/agentApi';
import { btn, input, Spinner, fmtDateTime } from '../agent/agentUi';
import AcordMappingEditor from './AcordMappingEditor';

const CATEGORIES = ['certificate', 'application', 'supplement', 'other'];

const Badge: React.FC<{ tone: 'green' | 'red' | 'amber' | 'gray' | 'blue' | 'purple'; children: React.ReactNode }> = ({ tone, children }) => {
  const cls = {
    green: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
    purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  }[tone];
  return <span className={`inline-block text-[11px] font-medium px-1.5 py-0.5 rounded ${cls}`}>{children}</span>;
};

const PageThumb: React.FC<{ formId: string; page: number }> = ({ formId, page }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let made: string | null = null;
    fetchBlobUrl(acordApi.adminPageUrl(formId, page))
      .then((u) => {
        made = u;
        setUrl(u);
      })
      .catch(() => setFailed(true));
    return () => {
      if (made) URL.revokeObjectURL(made);
    };
  }, [formId, page]);
  if (failed) return <div className="w-full aspect-[8.5/11] bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center text-xs text-gray-400">no preview</div>;
  return url ? (
    <a href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt={`Page ${page}`} className="w-full rounded border border-gray-200 dark:border-gray-700" /></a>
  ) : (
    <div className="w-full aspect-[8.5/11] bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center"><Spinner /></div>
  );
};

const UploadDialog: React.FC<{ onClose: () => void; onUploaded: (form: any) => void }> = ({ onClose, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  const [meta, setMeta] = useState({ formNumber: '', editionDate: '', title: '', category: 'certificate', notes: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
      const { form } = await acordApi.adminUpload(fd);
      onUploaded(form);
    } catch (err) {
      setError(apiErrorMessage(err, 'Upload failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg shadow-xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upload ACORD form</h3>
        <input type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
        <div className="grid grid-cols-2 gap-2 text-xs">
          <label><span className="text-gray-500">Form number</span><input className={input} placeholder="25" value={meta.formNumber} onChange={(e) => setMeta({ ...meta, formNumber: e.target.value })} /></label>
          <label><span className="text-gray-500">Edition date</span><input className={input} placeholder="2016/03" value={meta.editionDate} onChange={(e) => setMeta({ ...meta, editionDate: e.target.value })} /></label>
          <label className="col-span-2"><span className="text-gray-500">Title</span><input className={input} placeholder="Certificate of Liability Insurance" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} /></label>
          <label><span className="text-gray-500">Category</span>
            <select className={input} value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="col-span-2"><span className="text-gray-500">Notes</span><textarea className={input} rows={2} value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} /></label>
        </div>
        <p className="text-[11px] text-gray-500">Re-uploading the same form number + edition creates a new version and deactivates the previous one. XFA and encrypted PDFs are detected now and marked not fillable.</p>
        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
        <div className="flex justify-end gap-2">
          <button className={btn.secondary} onClick={onClose}>Cancel</button>
          <button className={btn.primary} onClick={submit} disabled={busy || !file || !meta.formNumber || !meta.editionDate || !meta.title}>{busy ? 'Uploading…' : 'Upload'}</button>
        </div>
      </div>
    </div>
  );
};

const LicensePanel: React.FC = () => {
  const [lic, setLic] = useState<{ licenseeName: string; licenseNumber: string; footerText: string; licenseTerms: string } | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    acordApi.license().then(setLic).catch(() => setLic({ licenseeName: '', licenseNumber: '', footerText: '', licenseTerms: '' }));
  }, []);
  if (!lic) return <Spinner />;
  return (
    <div className="space-y-2 text-xs max-w-2xl">
      <p className="text-gray-600 dark:text-gray-300">Blank forms are never served to tenants — only filled outputs. The footer is stamped on filled forms that have no licensee field ({'{licenseeName}'} and {'{licenseNumber}'} are substituted).</p>
      <div className="grid grid-cols-2 gap-2">
        <label><span className="text-gray-500">Licensee name</span><input className={input} value={lic.licenseeName} onChange={(e) => { setLic({ ...lic, licenseeName: e.target.value }); setSaved(false); }} /></label>
        <label><span className="text-gray-500">License number</span><input className={input} value={lic.licenseNumber} onChange={(e) => { setLic({ ...lic, licenseNumber: e.target.value }); setSaved(false); }} /></label>
      </div>
      <label className="block"><span className="text-gray-500">Footer text</span><input className={input} placeholder="Licensed to {licenseeName} — ACORD license #{licenseNumber}" value={lic.footerText} onChange={(e) => { setLic({ ...lic, footerText: e.target.value }); setSaved(false); }} /></label>
      <label className="block"><span className="text-gray-500">License terms (reference)</span><textarea className={input} rows={6} value={lic.licenseTerms} onChange={(e) => { setLic({ ...lic, licenseTerms: e.target.value }); setSaved(false); }} /></label>
      <button className={btn.primary} onClick={() => acordApi.saveLicense(lic).then(() => setSaved(true))}>Save</button>
      {saved && <span className="ml-2 text-green-700 dark:text-green-400">Saved.</span>}
    </div>
  );
};

const FormDetail: React.FC<{ formId: string; onBack: () => void; onChanged: () => void }> = ({ formId, onBack, onChanged }) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof acordApi.adminGet>> | null>(null);
  const [tab, setTab] = useState<'pages' | 'fields' | 'mapping' | 'versions'>('pages');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    acordApi.adminGet(formId).then(setData).catch((err) => setError(apiErrorMessage(err)));
  }, [formId]);
  useEffect(load, [load]);

  const toggleActive = async () => {
    if (!data) return;
    await acordApi.adminUpdate(formId, { isActive: !data.form.isActive });
    load();
    onChanged();
  };

  const download = async () => {
    try {
      const url = await fetchBlobUrl(acordApi.adminDownloadUrl(formId));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ACORD_${data?.form.formNumber}_v${data?.form.version}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      setError(apiErrorMessage(err, 'Download failed'));
    }
  };

  if (error) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!data) return <div className="flex items-center gap-2 text-gray-500"><Spinner /> Loading…</div>;
  const f = data.form;
  return (
    <div className="space-y-4">
      <button className="text-sm text-primary hover:underline" onClick={onBack}>← All forms</button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">ACORD {f.formNumber} — {f.title}</h3>
          <div className="flex flex-wrap gap-1.5 mt-1">
            <Badge tone="gray">Edition {f.editionDate}</Badge>
            <Badge tone="blue">v{f.version}</Badge>
            {f.isActive ? <Badge tone="green">Active</Badge> : <Badge tone="gray">Inactive</Badge>}
            {f.fillable ? <Badge tone="green">Fillable · {f.fieldCount} fields</Badge> : <Badge tone="red">{f.isXfa ? 'XFA — not fillable' : 'Not fillable'}</Badge>}
            <Badge tone={f.mappingStatus === 'verified' ? 'green' : f.mappingStatus === 'draft' ? 'amber' : 'gray'}>Mapping: {f.mappingStatus}</Badge>
            <Badge tone="purple">{data.fillCount} fill(s)</Badge>
          </div>
          {f.fillableMessage && <p className="text-xs text-red-600 dark:text-red-400 mt-2 max-w-2xl">{f.fillableMessage}</p>}
          <p className="text-[11px] text-gray-500 mt-1">sha256 {String(f.fileSha256).slice(0, 16)}… · {Math.round(f.fileSizeBytes / 1024)} KB · uploaded {fmtDateTime(f.createdAt)}</p>
        </div>
        <div className="flex gap-2">
          <button className={btn.secondary} onClick={download}>Download blank</button>
          <button className={f.isActive ? btn.danger : btn.primary} onClick={toggleActive}>{f.isActive ? 'Deactivate' : 'Activate'}</button>
        </div>
      </div>
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {(['pages', 'fields', 'mapping', 'versions'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === t ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}>{t[0].toUpperCase() + t.slice(1)}</button>
        ))}
      </div>
      {tab === 'pages' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: Math.min(f.pageCount, 12) }, (_, i) => i + 1).map((p) => (
            <div key={p}><PageThumb formId={f.id} page={p} /><div className="text-[11px] text-gray-500 text-center mt-1">Page {p}</div></div>
          ))}
        </div>
      )}
      {tab === 'fields' && (
        <div className="overflow-x-auto max-h-[60vh] border border-gray-200 dark:border-gray-700 rounded">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 sticky top-0">
              <tr><th className="text-left px-2 py-1.5">Field</th><th className="text-left px-2 py-1.5">Type</th><th className="text-left px-2 py-1.5">Page</th><th className="text-left px-2 py-1.5">Rect</th><th className="text-left px-2 py-1.5">Options / max</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {(f.fieldSchema || []).map((fld: any) => (
                <tr key={fld.name}>
                  <td className="px-2 py-1 font-mono">{fld.name}</td>
                  <td className="px-2 py-1">{fld.type}</td>
                  <td className="px-2 py-1">{fld.page ?? '—'}</td>
                  <td className="px-2 py-1 text-gray-500">{fld.rect ? fld.rect.join(', ') : '—'}</td>
                  <td className="px-2 py-1 text-gray-500">{fld.options?.length ? fld.options.join(' | ') : fld.maxLength ? `max ${fld.maxLength}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tab === 'mapping' && <AcordMappingEditor form={f} onSaved={() => { load(); onChanged(); }} />}
      {tab === 'versions' && (
        <ul className="text-sm divide-y divide-gray-100 dark:divide-gray-700">
          {data.versions.map((v: any) => (
            <li key={v.id} className="py-2 flex items-center gap-3">
              <span className="font-medium">v{v.version}</span>
              {v.isActive && <Badge tone="green">Active</Badge>}
              <span className="text-xs text-gray-500">{fmtDateTime(v.createdAt)} · {v.fieldCount} fields · mapping {v.mappingStatus} · sha {String(v.fileSha256).slice(0, 10)}…</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Sysadmin ACORD Forms Repository (AGENT_MAILBOX_PLAN.md §8.3, Phases 1A + 4). */
const AcordRepositoryAdmin: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [forms, setForms] = useState<any[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [view, setView] = useState<'forms' | 'license'>('forms');
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(() => {
    acordApi.adminList().then((r) => setForms(r.forms)).catch((err) => setError(apiErrorMessage(err)));
  }, []);
  useEffect(load, [load]);

  const visible = (forms || []).filter((f) => showInactive || f.isActive);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg shadow-xl max-w-7xl w-full h-5/6 m-4 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">ACORD Forms Repository</h2>
            <div className="flex gap-1">
              {(['forms', 'license'] as const).map((v) => (
                <button key={v} onClick={() => setView(v)} className={`text-xs px-2.5 py-1 rounded-full border ${view === v ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>{v === 'forms' ? 'Forms' : 'License'}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {view === 'forms' && !selected && <button className={btn.primary} onClick={() => setShowUpload(true)}>Upload form</button>}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" aria-label="Close">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>}
          {view === 'license' ? (
            <LicensePanel />
          ) : selected ? (
            <FormDetail formId={selected} onBack={() => setSelected(null)} onChanged={load} />
          ) : !forms ? (
            <div className="flex items-center gap-2 text-gray-500"><Spinner /> Loading…</div>
          ) : (
            <>
              <label className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1.5 mb-2"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive versions</label>
              {visible.length === 0 ? (
                <p className="text-sm text-gray-500">No forms uploaded yet. Upload the fillable ACORD PDFs as licensing delivers them.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700">
                    <tr><th className="text-left py-2">Form</th><th className="text-left py-2">Edition</th><th className="text-left py-2">Status</th><th className="text-left py-2">Mapping</th><th className="text-left py-2">Fills</th><th className="text-left py-2">Uploaded</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {visible.map((f) => (
                      <tr key={f.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer" onClick={() => setSelected(f.id)}>
                        <td className="py-2"><div className="font-medium text-gray-900 dark:text-white">ACORD {f.formNumber}</div><div className="text-xs text-gray-500">{f.title}</div></td>
                        <td className="py-2 text-gray-700 dark:text-gray-200">{f.editionDate} <span className="text-xs text-gray-500">v{f.version}</span></td>
                        <td className="py-2 space-x-1">
                          {f.isActive ? <Badge tone="green">Active</Badge> : <Badge tone="gray">Inactive</Badge>}
                          {f.isXfa ? <Badge tone="red">XFA</Badge> : f.fillable ? <Badge tone="blue">{f.fieldCount} fields</Badge> : <Badge tone="red">Not fillable</Badge>}
                        </td>
                        <td className="py-2"><Badge tone={f.mappingStatus === 'verified' ? 'green' : f.mappingStatus === 'draft' ? 'amber' : 'gray'}>{f.mappingStatus} · {f.mappedKeyCount}</Badge></td>
                        <td className="py-2 text-gray-700 dark:text-gray-200">{f.fillCount}</td>
                        <td className="py-2 text-xs text-gray-500">{fmtDateTime(f.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
      {showUpload && <UploadDialog onClose={() => setShowUpload(false)} onUploaded={(form) => { setShowUpload(false); load(); setSelected(form.id); }} />}
    </div>
  );
};

export default AcordRepositoryAdmin;
